import { Box, Paper, Typography } from '@mui/material';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ReactNode } from 'react';

interface StatCardProps {
  label: string;
  value: number | string;
  icon: ReactNode;
  color: string;
  trend?: { value: number; label: string };
  loading?: boolean;
}

export function StatCard({ label, value, icon, color, trend, loading }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        border: '1px solid rgba(255,255,255,0.06)',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 24px ${color}22`,
        },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: `linear-gradient(90deg, ${color}, transparent)`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1 }}>
          {label}
        </Typography>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            backgroundColor: `${color}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>

      <Typography
        variant="h3"
        sx={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: loading ? 'text.disabled' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {loading ? '—' : value}
      </Typography>

      {trend && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trend.value > 0 ? (
            <TrendingUp size={13} color="#10B981" />
          ) : trend.value < 0 ? (
            <TrendingDown size={13} color="#EF4444" />
          ) : (
            <Minus size={13} color="#64748B" />
          )}
          <Typography
            variant="caption"
            sx={{
              color:
                trend.value > 0 ? '#10B981' : trend.value < 0 ? '#EF4444' : 'text.secondary',
            }}
          >
            {trend.label}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
